import React, { useMemo, useState } from 'react';
import { Field, Input, Textarea, Button, Badge, Row, SaveBar, uid, move } from './ui';
import useDraft from './useDraft';
import { formatDate, todayIso, byDateDesc } from '../lib/format';
import { daysWithoutReport, draftReportFor } from '../lib/reportDraft';

export default function ReportsEditor({ stored, karte = [], boce = [] }) {
  const { draft, setDraft, dirty, saving, error, save, reset } = useDraft('reports', stored);
  const [openId, setOpenId] = useState(null);

  const sorted = useMemo(() => [...draft].sort(byDateDesc), [draft]);
  const missing = useMemo(() => daysWithoutReport(karte, boce, draft), [karte, boce, draft]);

  const patch = (id, next) => setDraft((d) => d.map((r) => (r.id === id ? next : r)));
  const remove = (id) => {
    if (!window.confirm('Obrisati ovaj zapisnik?')) return;
    setDraft((d) => d.filter((r) => r.id !== id));
  };

  const add = () => {
    const r = { id: uid('r'), date: todayIso(), title: '', intro: '', paragraphs: [''] };
    setDraft((d) => [r, ...d]);
    setOpenId(r.id);
  };

  // Start a report for a day that has results but no write-up, pre-filled with
  // that day's scores so there's something to rewrite rather than a blank box.
  const addForDay = (date) => {
    const r = { id: uid('r'), ...draftReportFor(date, karte, boce) };
    setDraft((d) => [r, ...d]);
    setOpenId(r.id);
  };

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <Button variant="dark" onClick={add}>+ Novi zapisnik</Button>
        <span className="text-xs text-slate-500">{draft.length} zapisnika</span>
      </div>

      {missing.length > 0 && (
        <div className="mb-4 rounded-lg border border-amber-300 bg-amber-50 p-3">
          <p className="mb-2 text-xs font-bold uppercase tracking-wide text-amber-900">
            {missing.length} {missing.length === 1 ? 'dan nema' : 'dana nemaju'} zapisnik
          </p>
          <div className="flex flex-wrap gap-1.5">
            {missing.map((d) => (
              <Button key={d.date} className="!py-1 !text-xs" onClick={() => addForDay(d.date)}>
                {formatDate(d.date)}
                <span className="ml-1 text-slate-400">
                  {[d.karte && `${d.karte}K`, d.boce && `${d.boce}B`].filter(Boolean).join(' ')}
                </span>
              </Button>
            ))}
          </div>
          <p className="mt-2 text-[11px] text-amber-700">
            Klikni datum — otvorit će se novi zapisnik s već upisanim rezultatima toga dana,
            pa to prepišeš svojim riječima.
          </p>
        </div>
      )}

      <div className="space-y-2">
        {sorted.map((r) => (
          <Row
            key={r.id}
            open={openId === r.id}
            onToggle={() => setOpenId(openId === r.id ? null : r.id)}
            summary={r.title || '(bez naslova)'}
            meta={
              <>
                <Badge>{formatDate(r.date)}</Badge>
                <Badge>{(r.paragraphs || []).length} odlomaka</Badge>
                {!r.title && <Badge tone="red">nema naslova</Badge>}
              </>
            }
            onDelete={() => remove(r.id)}
          >
            <div className="space-y-4">
              <div className="grid gap-3 sm:grid-cols-[180px_1fr]">
                <Field label="Datum">
                  <Input type="date" value={r.date} onChange={(e) => patch(r.id, { ...r, date: e.target.value })} />
                </Field>
                <Field label="Naslov" hint="Prikazuje se velikim slovima.">
                  <Input value={r.title} onChange={(e) => patch(r.id, { ...r, title: e.target.value })} />
                </Field>
              </div>

              <Field label="Uvod" hint="Podebljani kurziv ispod naslova.">
                <Textarea value={r.intro} onChange={(e) => patch(r.id, { ...r, intro: e.target.value })} />
              </Field>

              <Field label="Odlomci">
                <div className="space-y-2">
                  {(r.paragraphs || []).map((p, pi) => (
                    <div key={pi} className="flex items-start gap-2">
                      <span className="mt-2 w-5 text-right text-xs font-bold text-slate-400">{pi + 1}.</span>
                      <Textarea
                        value={p}
                        onChange={(e) => patch(r.id, {
                          ...r,
                          paragraphs: r.paragraphs.map((x, i) => (i === pi ? e.target.value : x)),
                        })}
                      />
                      <div className="flex flex-col gap-1">
                        <Button className="!px-2 !py-0.5" onClick={() => patch(r.id, { ...r, paragraphs: move(r.paragraphs, pi, pi - 1) })}>↑</Button>
                        <Button className="!px-2 !py-0.5" onClick={() => patch(r.id, { ...r, paragraphs: move(r.paragraphs, pi, pi + 1) })}>↓</Button>
                        <Button variant="danger" className="!px-2 !py-0.5" onClick={() => patch(r.id, { ...r, paragraphs: r.paragraphs.filter((_, i) => i !== pi) })}>✕</Button>
                      </div>
                    </div>
                  ))}
                  <Button onClick={() => patch(r.id, { ...r, paragraphs: [...(r.paragraphs || []), ''] })}>+ Odlomak</Button>
                </div>
              </Field>
            </div>
          </Row>
        ))}
      </div>

      <SaveBar dirty={dirty} saving={saving} error={error} onSave={save} onReset={reset} count={draft.length} />
    </div>
  );
}
